import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { LogOut, User, Bell, Settings, ChevronRight, Sparkles, Brain } from 'lucide-react';
import EditProfileModal from '../components/EditProfileModal';
import NotificationSettings from '../components/NotificationSettings';
import VoiceCommands from '../components/VoiceCommands';
import NotificationOnboarding from '../components/NotificationOnboarding';
import SmartDashboard from '../components/SmartDashboard';
import CalendarConnectPrompt, { useCalendarPrompt } from '../components/CalendarConnectPrompt';
import QuickActionButton from '../components/QuickActionButton';

const Home = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, getEmoji } = useTheme();
  const { t, getGreeting: getLocalizedGreeting } = useLocale();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [greeting, setGreeting] = useState('');
  const [greetingSubtitle, setGreetingSubtitle] = useState('');
  const [gentleNudge, setGentleNudge] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [surfacedMemories, setSurfacedMemories] = useState([]);
  const [streaks, setStreaks] = useState(null);
  const [eveningCheckin, setEveningCheckin] = useState(null);
  const [showEveningModal, setShowEveningModal] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  
  // New emotional partner states
  const [alloraBanner, setAlloraBanner] = useState({ text: null, emoji: "💜" });
  const [dailySuggestion, setDailySuggestion] = useState(null);
  
  // 💜 Emotional Intelligence Engine - Daily Rituals (REMOVED - was showing at wrong times)

  // 📅 Google Calendar Connect Prompt
  const { showPrompt: showCalendarPrompt, closePrompt: closeCalendarPrompt, calendarConnected } = useCalendarPrompt();

  // 🔔 Listen for auto-subscription results from login
  useEffect(() => {
    const handleNotificationStatus = async (event) => {
      const result = event.detail;
      console.log('🔔 Notification status event received:', result);
      
      if (result.permissionDenied) {
        // Notifications are blocked - user needs to enable from browser settings
        toast.error('Notifications blocked', {
          description: 'Enable notifications in your browser settings to receive reminders.',
          duration: 8000,
          action: {
            label: 'How?',
            onClick: () => {
              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
              if (isIOS) {
                toast.info('iOS Settings', {
                  description: 'Go to Settings → Safari → Notifications → Allow',
                  duration: 10000
                });
              } else {
                toast.info('Browser Settings', {
                  description: 'Click the lock icon in the URL bar and enable notifications',
                  duration: 10000
                });
              }
            }
          }
        });
      } else if (result.needsUserGesture) {
        // Permission not asked yet - NotificationOnboarding modal will handle this
        // Don't show toast to avoid duplicate prompts
        console.log('🔔 Notification permission needed - modal will show');
      } else if (result.success && result.subscribed) {
        // Already subscribed or just subscribed successfully
        toast.success('Notifications enabled! 🔔', {
          description: "You'll receive reminders and updates.",
          duration: 4000
        });
      }
    };

    window.addEventListener('lifeflo-notification-status', handleNotificationStatus);
    return () => {
      window.removeEventListener('lifeflo-notification-status', handleNotificationStatus);
    };
  }, []);

  // 🚨 EMOTION-FIRST NUDGE GENERATION
  // RULES:
  // 1. NEVER make water the first interaction
  // 2. Emotional support ALWAYS outranks health metrics
  // 3. Metrics are SUPPORTING DATA - never the opener
  const generateGentleNudge = () => {
    const hour = new Date().getHours();
    let nudge = null;
    
    // Late night support (10pm - 5am) - EMOTIONAL FIRST
    if (hour >= 22 || hour < 5) {
      nudge = {
        emoji: '💜',
        message: t('home.cantSleep'),
        action: () => navigate('/chat')
      };
    }
    // Morning - supportive start (NOT task-focused)
    else if (hour >= 5 && hour < 9) {
      nudge = {
        emoji: '☀️',
        message: t('home.letsTakeCare'),
        action: () => navigate('/chat')
      };
    }
    // Mid-morning check-in
    else if (hour >= 9 && hour < 12) {
      nudge = {
        emoji: '🌱',
        message: t('home.howAreYouDoing'),
        action: () => navigate('/chat')
      };
    }
    // Afternoon - gentle presence
    else if (hour >= 12 && hour < 17) {
      nudge = {
        emoji: '✨',
        message: t('home.afternoonCheckin'),
        action: () => navigate('/chat')
      };
    }
    // Evening wind-down (NOT task review)
    else if (hour >= 17 && hour < 22) {
      nudge = {
        emoji: '🌙',
        message: t('home.windingDownRest'),
        action: null
      };
    }
    // Default warm nudge
    else {
      nudge = {
        emoji: '💜',
        message: t('home.imHereNoRush'),
        action: () => navigate('/chat')
      };
    }
    
    setGentleNudge(nudge);
  };

  // Set greeting from dashboard or use default emotion-first greeting
  useEffect(() => {
    if (dashboardData?.greeting) {
      setGreeting(dashboardData.greeting);
    } else if (user) {
      // Fallback: Emotion-first greeting (NOT metric-based)
      const hour = new Date().getHours();
      const firstName = user?.name?.split(' ')[0] || '';
      
      if (hour >= 22 || hour < 5) {
        setGreeting(`${t('home.heyName').replace('{name}', firstName)} 💜`);
      } else if (hour < 9) {
        setGreeting(`${t('home.goodMorning')}, ${firstName}. ${t('home.readyWhenYouAre')}`);
      } else if (hour < 12) {
        setGreeting(`${t('home.morning')}, ${firstName}. ${t('home.howAreYou')}`);
      } else if (hour < 17) {
        setGreeting(`${t('home.heyName').replace('{name}', firstName)}`);
      } else if (hour < 22) {
        setGreeting(`${t('home.evening')}, ${firstName}. ${t('home.windingDown')}`);
      } else {
        setGreeting(`${t('home.lateNight')}, ${firstName}. 💜`);
      }
    }
    
    // Set greeting subtitle
    if (dashboardData?.greeting_subtitle) {
      setGreetingSubtitle(dashboardData.greeting_subtitle);
    }
    
    // Set dynamic Allora banner
    if (dashboardData?.allora_banner) {
      setAlloraBanner(dashboardData.allora_banner);
    }
    
    // Set daily suggestion
    if (dashboardData?.daily_suggestion) {
      setDailySuggestion(dashboardData.daily_suggestion);
    }
  }, [dashboardData, user]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // 🚨 PRIORITY: Load emotion-first smart dashboard
        const smartDashRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/home/smart-dashboard`,
          { credentials: 'include' }
        );
        if (smartDashRes.ok) {
          const smartDashData = await smartDashRes.json();
          setDashboardData(smartDashData.dashboard);
          
          // Use smart-dashboard data for todayData as well
          const progress = smartDashData.dashboard?.progress || {};
          setTodayData({
            tasks: progress.tasks_pending || 0,
            water: progress.water_glasses || 0,
            waterGoal: progress.water_goal || 8,
            bills: progress.bills_due || 0
          });
          
          // Generate gentle nudge (emotion-first)
          generateGentleNudge();
        }
        
        // Load additional dashboard data
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/assistant/dashboard`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setTodayData({
            tasks: data.quick_stats?.work?.pending_tasks || 0,
            water: data.quick_stats?.health?.water_today || 0,
            waterGoal: data.quick_stats?.health?.water_goal || 8,
            bills: data.quick_stats?.life?.upcoming_bills || 0
          });
        }
        
        // Load morning briefing (if morning)
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) {
          const briefingRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/allora/briefing`,
            { credentials: 'include' }
          );
          if (briefingRes.ok) {
            const briefingData = await briefingRes.json();
            setBriefing(briefingData);
          }
        }
        
        // Load surfaced memories
        const memoriesRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/allora/surfaced-memories`,
          { credentials: 'include' }
        );
        if (memoriesRes.ok) {
          const memoriesData = await memoriesRes.json();
          setSurfacedMemories(memoriesData.memories || []);
        }
        
        // Load streaks
        const streaksRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/allora/streaks`,
          { credentials: 'include' }
        );
        if (streaksRes.ok) {
          const streaksData = await streaksRes.json();
          setStreaks(streaksData.streaks || null);
        }
        
        // Load evening check-in (only show between 7 PM - 11 PM)
        if (hour >= 19 && hour < 23) {
          const eveningRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/allora/evening-checkin`,
            { credentials: 'include' }
          );
          if (eveningRes.ok) {
            const eveningData = await eveningRes.json();
            setEveningCheckin(eveningData);
          }
        }
        
        // 💜 Daily Ritual check REMOVED - was triggering at wrong times
        // Users can log mood manually via Quick Actions button
      } catch (error) {
        console.error('Error loading data:', error);
        setGentleNudge({
          emoji: '💜',
          message: t('home.imHereNoRush'),
          action: null
        });
      }
    };
    
    if (user) {
      loadData();
    }
  }, [user]);
  
  // Handle mood selection from evening check-in
  const handleMoodSelect = async (mood) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/allora/mood-log`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mood })
        }
      );
      if (response.ok) {
        const data = await response.json();
        setShowEveningModal(false);
        setEveningCheckin(null);
        toast.success(data.response);
      }
    } catch (error) {
      console.error('Error logging mood:', error);
      toast.error(t('home.failedLogMood'));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  // Theme-based background gradient class
  const bgGradientClass = theme?.colors?.gradientBg 
    ? `bg-gradient-to-br ${theme.colors.gradientBg}` 
    : 'bg-gradient-to-br from-teal-50/60 via-cyan-50/40 to-white';

  return (
    <div className={`min-h-screen ${bgGradientClass}`} style={{ background: `linear-gradient(135deg, ${theme?.colors?.primary}08, ${theme?.colors?.accent}05, white)` }}>
      {/* 📅 Google Calendar Connect Prompt - First Launch */}
      <CalendarConnectPrompt 
        isOpen={showCalendarPrompt} 
        onClose={closeCalendarPrompt}
        onSkip={closeCalendarPrompt}
      />
      
      {/* Header */}
      <header className="px-4 py-4 flex items-center justify-between glass-strong sticky top-0 z-50 border-b border-white/40">
        <div className="flex items-center gap-2.5">
          <img 
            src="/assets/lifeflo-logo-new.png" 
            alt="LifeFlo" 
            className="w-9 h-9 rounded-xl object-cover shadow-soft transition-transform hover:scale-105"
          />
          <span className="text-lg font-semibold" style={{ color: theme?.colors?.primary }}>
            LifeFlo
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <VoiceCommands />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="rounded-full w-9 h-9 p-0 transition-colors" style={{ '--hover-bg': `${theme?.colors?.primary}15` }}>
                <User className="h-5 w-5 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 rounded-xl shadow-soft-lg border-gray-100/80 p-1">
              <DropdownMenuLabel className="font-normal px-3 py-2">
                <p className="text-sm font-semibold text-gray-800">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gray-100/60" />
              <DropdownMenuItem onClick={() => setShowNotificationSettings(true)} className="rounded-lg mx-1 cursor-pointer">
                <Bell className="mr-2 h-4 w-4 text-gray-500" />
                {t('settings.notifications')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="rounded-lg mx-1 cursor-pointer">
                <Settings className="mr-2 h-4 w-4 text-gray-500" />
                {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowEditProfile(true)} className="rounded-lg mx-1 cursor-pointer">
                <User className="mr-2 h-4 w-4 text-gray-500" />
                {t('settings.profile')}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-100/60" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 rounded-lg mx-1 cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                {t('settings.logOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pb-8 pt-2">
        {/* 1. EMOTIONAL GREETING */}
        <div className="mb-4 animate-fade-in">
          <h1 className="text-2xl font-semibold text-gray-800 tracking-tight">
            {greeting}
          </h1>
          <p className="text-gray-400 mt-1 text-sm">
            {greetingSubtitle || (dashboardData?.emotional_mode || dashboardData?.ambient_emotional
              ? t('home.youDontHaveToCarry')
              : t('home.imHereToHelp'))}
          </p>
        </div>

        {/* 3. DYNAMIC TALK TO ALLORA BANNER - Goes to dedicated Allora chat */}
        <button
          onClick={() => navigate('/allora-chat')}
          className="w-full glass-strong rounded-2xl p-5 shadow-soft border border-white/60 mb-4 text-left animate-fade-in-up hover:shadow-soft-lg hover:border-purple-200/60 hover:-translate-y-0.5 transition-all duration-200"
          style={{animationDelay: '0.05s'}}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-soft">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                {t('cards.talkToAllora') || 'Talk to Allora'}
                <span className="text-lg">{alloraBanner.emoji || '💜'}</span>
              </h3>
              <p className="text-sm text-gray-500">{alloraBanner.text || t('home.imHere')}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </button>

        {/* NOTE: Daily Suggestion and Gentle Nudge cards removed to simplify UI
            User can access suggestions through the Allora chat or Suggested Feed page */}

        {/* 6. FOUR MODE CARDS - Emotional Subtitles */}
        <div className="grid grid-cols-2 gap-3 mb-6" style={{animationDelay: '0.15s'}}>
          <button
            onClick={() => navigate('/chat')}
            className="glass-strong rounded-2xl p-5 shadow-soft border border-white/60 hover:border-cyan-200/60 hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-200 text-left group"
          >
            <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">💬</div>
            <h3 className="font-semibold text-gray-800">{t('nav.yourspace') || 'YourSpace'}</h3>
            <p className="text-xs text-gray-400">{t('cards.yourspaceDesc') || 'General AI chat'}</p>
          </button>

          <button
            onClick={() => navigate('/work')}
            className="glass-strong rounded-2xl p-5 shadow-soft border border-white/60 hover:border-blue-200/60 hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-200 text-left group"
          >
            <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">💼</div>
            <h3 className="font-semibold text-gray-800">{t('nav.work')}</h3>
            <p className="text-xs text-gray-400">{t('cards.workDesc')}</p>
          </button>

          <button
            onClick={() => navigate('/health')}
            className="glass-strong rounded-2xl p-5 shadow-soft border border-white/60 hover:border-green-200/60 hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-200 text-left group"
          >
            <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">💪</div>
            <h3 className="font-semibold text-gray-800">{t('nav.health')}</h3>
            <p className="text-xs text-gray-400">{t('cards.healthDesc')}</p>
          </button>

          <button
            onClick={() => navigate('/life')}
            className="glass-strong rounded-2xl p-5 shadow-soft border border-white/60 hover:border-teal-200/60 hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-200 text-left group"
          >
            <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">💰</div>
            <h3 className="font-semibold text-gray-800">{t('nav.life')}</h3>
            <p className="text-xs text-gray-400">{t('cards.lifeDesc')}</p>
          </button>
        </div>

        {/* 🏠 Smart Dashboard - Comprehensive engagement features */}
        <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <SmartDashboard />
        </div>

        {/* 🧠 Allora Remembers... (Memory Surfacing - "Aha!" Moment) */}
        {surfacedMemories.length > 0 && (
          <div className="mt-5 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <div className="glass-strong rounded-2xl p-4 shadow-soft" style={{ borderColor: `${theme?.colors?.primary}20` }}>
              <div className="flex items-center gap-2 mb-3">
                <Brain className="h-4 w-4" style={{ color: theme?.colors?.primary }} />
                <h2 className="text-sm font-medium" style={{ color: theme?.colors?.primary }}>{t('home.alloraRemembers')}</h2>
              </div>
              <div className="space-y-2">
                {surfacedMemories.map((memory, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-white/60 rounded-xl"
                  >
                    <span className="text-lg">{memory.icon}</span>
                    <div>
                      <p className="text-xs font-medium" style={{ color: theme?.colors?.primary }}>{memory.title}</p>
                      <p className="text-sm text-gray-700 mt-0.5">{memory.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ☀️ Morning Briefing - Only show in morning (5 AM - 12 PM) */}
        {briefing && new Date().getHours() >= 5 && new Date().getHours() < 12 && (
          <div className="mt-5 glass-strong rounded-2xl p-4 shadow-soft border border-yellow-200/40 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">☀️</span>
              <div>
                <p className="font-semibold text-gray-800">{t('home.morningBriefing')}</p>
                <p className="text-xs text-gray-500">{t('home.whatsOnPlate')}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              {briefing.tasks_today > 0 && (
                <p>📋 {briefing.tasks_today} {t('home.tasksToComplete')}</p>
              )}
              {briefing.bills_due > 0 && (
                <p>💰 {briefing.bills_due} {t('home.billsDueSoon')}</p>
              )}
              {briefing.appointments_today > 0 && (
                <p>📅 {briefing.appointments_today} {t('home.appointmentsToday')}</p>
              )}
              {briefing.water_goal && (
                <p>💧 {t('home.rememberWater')} ({briefing.water_goal} {t('common.glasses')})</p>
              )}
              {!briefing.tasks_today && !briefing.bills_due && !briefing.appointments_today && (
                <p>✨ {t('home.allCaughtUp')} {t('home.enjoyMorning')}</p>
              )}
            </div>
          </div>
        )}

        {/* 🌙 Evening Check-in Prompt - Only show in evening (7 PM - 11 PM) */}
        {eveningCheckin && !showEveningModal && (
          <button
            onClick={() => setShowEveningModal(true)}
            className="mt-5 w-full glass-strong rounded-2xl p-4 shadow-soft border border-teal-200/40 text-left animate-fade-in-up hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-200"
            style={{animationDelay: '0.3s'}}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🌙</span>
              <div>
                <p className="font-semibold text-gray-800">{t('home.eveningReflection')}</p>
                <p className="text-xs text-gray-500">{t('home.howWasYourDay')}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </div>
          </button>
        )}

        {/* Warm footer message */}
        <p className="text-xs text-gray-400 mt-8 text-center font-light animate-fade-in" style={{animationDelay: '0.35s'}}>
          {t('home.smallStepsCount') || 'Small steps count. I\'m here for you.'} 💜
        </p>
      </main>

      {/* 🌙 Evening Check-in Modal */}
      {showEveningModal && eveningCheckin && (
        <Dialog open={showEveningModal} onOpenChange={setShowEveningModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{eveningCheckin.title}</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-gray-600 whitespace-pre-line mb-4">
              {eveningCheckin.message}
            </div>
            
            <p className="text-sm font-medium text-gray-700 mb-3">{eveningCheckin.mood_prompt}</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {eveningCheckin.mood_options?.map((option) => (
                <Button
                  key={option.value}
                  variant="outline"
                  className="flex flex-col items-center p-3 h-auto hover:bg-teal-50 active:bg-teal-100"
                  onClick={() => handleMoodSelect(option.value)}
                >
                  <span className="text-2xl mb-1">{option.emoji}</span>
                  <span className="text-xs text-gray-600">{option.label}</span>
                </Button>
              ))}
            </div>
            
            <Button
              variant="ghost"
              className="w-full text-sm text-gray-500"
              onClick={() => setShowEveningModal(false)}
            >
              {t('home.maybeLater')}
            </Button>
          </DialogContent>
        </Dialog>
      )}

      {/* Notification Onboarding Prompt */}
      <NotificationOnboarding />

      {/* Modals */}
      <EditProfileModal
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
      />
      <NotificationSettings 
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
      />
      
      {/* Quick Action Button - Floating + button */}
      <QuickActionButton />
    </div>
  );
};

export default Home;
